import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { PhotoLightbox } from "./PhotoLightbox";
import {
  CATEGORY_LABELS,
  galleryPhotos,
  type PhotoCategory,
  type PhotoItem,
} from "@/content/photo-gallery";

type Filter = "alla" | PhotoCategory;

type Props = {
  /** Bilder att visa. Default = hela galleriet. */
  photos?: PhotoItem[];
  /** Visa kategoriflikar. */
  filterable?: boolean;
  /** Max antal bilder som visas innan "Visa fler". */
  initial?: number;
  className?: string;
  heading?: string;
  eyebrow?: string;
  lede?: string;
};

/**
 * Masonry-galleri med naturliga bildproportioner, blur-up och lightbox.
 * Kolumnlayouten använder CSS `columns` så att inget beskärs.
 */
export function PhotoMasonry({
  photos = galleryPhotos,
  filterable = true,
  initial = 15,
  className,
  heading,
  eyebrow,
  lede,
}: Props) {
  const [filter, setFilter] = useState<Filter>("alla");
  const [expanded, setExpanded] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});

  const filtered = useMemo(
    () => (filter === "alla" ? photos : photos.filter((p) => p.category === filter)),
    [photos, filter],
  );
  const visible = expanded ? filtered : filtered.slice(0, initial);

  const tabs: Filter[] = ["alla", "portratt", "kampanj", "studio"];

  return (
    <div className={cn("container-wide", className)}>
      {(eyebrow || heading || lede) && (
        <div className="mb-8 max-w-3xl">
          {eyebrow && (
            <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">{eyebrow}</div>
          )}
          {heading && (
            <h2 className="text-balance text-4xl font-semibold leading-[1.05] md:text-5xl">
              {heading}
            </h2>
          )}
          {lede && <p className="mt-5 text-pretty text-muted-foreground md:text-lg">{lede}</p>}
        </div>
      )}

      {filterable && (
        <div className="mb-8 flex flex-wrap gap-2" role="tablist" aria-label="Filtrera bilder">
          {tabs.map((t) => {
            const active = filter === t;
            return (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setFilter(t);
                  setExpanded(false);
                }}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm transition",
                  active
                    ? "border-mint/60 bg-mint/10 text-mint"
                    : "border-white/10 bg-surface/60 text-muted-foreground hover:text-foreground",
                )}
              >
                {t === "alla" ? "Alla bilder" : CATEGORY_LABELS[t]}
              </button>
            );
          })}
        </div>
      )}

      <div className="columns-2 gap-3 [column-fill:_balance] md:columns-3 md:gap-4 lg:columns-4">
        {visible.map((p, i) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setOpenIndex(filtered.indexOf(p))}
            aria-label={`Öppna bild: ${p.alt}`}
            className="group mb-3 block w-full overflow-hidden rounded-xl bg-surface md:mb-4 md:rounded-2xl"
          >
            <img
              src={p.src}
              width={p.w}
              height={p.h}
              alt={p.alt}
              loading={i < 4 ? "eager" : "lazy"}
              decoding="async"
              onLoad={() => setLoaded((s) => ({ ...s, [p.id]: true }))}
              className={cn(
                "h-auto w-full object-cover transition duration-700 group-hover:scale-[1.03]",
                loaded[p.id] ? "opacity-100 blur-0" : "opacity-0 blur-md",
              )}
            />
          </button>
        ))}
      </div>

      {!expanded && filtered.length > initial && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="rounded-full border border-white/15 bg-surface/60 px-6 py-3 text-sm transition hover:border-mint/50 hover:text-mint"
          >
            Visa alla {filtered.length} bilder
          </button>
        </div>
      )}

      <PhotoLightbox
        photos={filtered}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onIndexChange={setOpenIndex}
      />
    </div>
  );
}
